import { useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, Play, Star, Quote } from "lucide-react";
import Header from "@/components/Header";
import QuoteBanner from "@/components/QuoteBanner";
import Footer from "@/components/Footer";
import { useLang } from "@/context/LangContext";

// Media mentions data — English + Tamil descriptors
const FEATURED = {
  key: "girish",
  source: "LinkedIn",
  author: "Girish Mathrubootham",
  role_en: "Founder & Executive Chairman, Freshworks",
  role_ta: "நிறுவனர் & நிர்வாக தலைவர், Freshworks",
  title_en:
    "\"Really cool product from Kapil, a class 12 student. Kudos!\"",
  title_ta:
    "\"12ஆம் வகுப்பு மாணவர் கபிலிடமிருந்து அற்புதமான தயாரிப்பு. வாழ்த்துகள்!\"",
  url:
    "https://www.linkedin.com/posts/girish1_neeyosi-know-your-candidate-activity-7452936904209252352-OU8D",
  domain: "linkedin.com",
  color: "#0a66c2",
};

const ITEMS = [
  {
    key: "hindu",
    source: "The Hindu BusinessLine",
    title_en:
      "AI-powered neeyosi.com puts candidate info at your fingertips",
    title_ta:
      "AI-இயங்கும் neeyosi.com — வேட்பாளர் தகவல்கள் உங்கள் விரல் நுனியில்",
    blurb_en:
      "Featured in the tech section — how a Chennai teen used AI + open data to build a voter tool.",
    blurb_ta:
      "தொழில்நுட்பப் பகுதியில் — சென்னை மாணவர் AI + திறந்த தரவைக் கொண்டு வாக்காளர் கருவியை உருவாக்கிய கதை.",
    url:
      "https://www.thehindubusinessline.com/info-tech/ai-powered-neeyosicom-puts-candidate-info-at-your-fingertips/article70893116.ece",
    domain: "thehindubusinessline.com",
    color: "#00568D",
  },
  {
    key: "ndtv",
    source: "NDTV",
    title_en:
      "How A Teen's Website Helped Tamil Nadu Voters Know About Candidates",
    title_ta:
      "ஒரு பதின்ம வயதினரின் இணையதளம் தமிழக வாக்காளர்களுக்கு எப்படி உதவியது",
    blurb_en:
      "National coverage from NDTV — the story behind NeeYosi and its impact.",
    blurb_ta:
      "NDTV-இல் தேசிய அளவிலான செய்தி — NeeYosi பின்னணி மற்றும் அதன் தாக்கம்.",
    url:
      "https://www.ndtv.com/india-news/how-a-teens-website-helped-tamil-nadu-voters-know-about-candidates-11405816",
    domain: "ndtv.com",
    color: "#d40e2b",
  },
  {
    key: "ndtv-yt",
    source: "NDTV · YouTube",
    title_en: "Watch the NDTV interview (from 23:00)",
    title_ta: "NDTV நேர்காணலைப் பாருங்கள் (23:00 முதல்)",
    blurb_en:
      "Live segment on NDTV — Kapil Bhaskar explains why he built neeyosi.com.",
    blurb_ta:
      "NDTV நேரடி நிகழ்ச்சி — நீயோசியை உருவாக்கிய காரணத்தை கபில் பாஸ்கர் விளக்குகிறார்.",
    url: "https://www.youtube.com/watch?v=sCZw0n7C1O4",
    domain: "youtube.com",
    color: "#ff0000",
    isVideo: true,
  },
  {
    key: "yocee",
    source: "YoCee",
    title_en: "NeeYosi — A Youth's Call For Informed Voting",
    title_ta: "நீயோசி — தகவலறிந்த வாக்களிப்புக்கான ஒரு இளைஞனின் அழைப்பு",
    blurb_en:
      "YoCee spotlights the youth-driven civic-tech story behind NeeYosi.",
    blurb_ta:
      "YoCee — நீயோசியின் இளைஞர்-தலைமையிலான குடிமை-தொழில்நுட்பக் கதையை முன்னிலைப்படுத்துகிறது.",
    url: "https://yocee.in/neeyosi-a-youths-call-for-informed-voting",
    domain: "yocee.in",
    color: "#f39c12",
  },
  {
    key: "mylapore",
    source: "Mylapore Times",
    title_en:
      "Vidya Mandir's Kapil Bhaskar builds voter info platform — users flowing in on polling eve",
    title_ta:
      "வித்யா மந்திரின் கபில் பாஸ்கர் வாக்காளர் தகவல் தளத்தை உருவாக்குகிறார் — வாக்குப்பதிவு முன்நாள் பயனர்கள் அதிகரிப்பு",
    blurb_en:
      "Hyper-local Mylapore paper covers Kapil's launch and its polling-eve traction.",
    blurb_ta:
      "மயிலாப்பூர் உள்ளூர் நாளிதழ் — கபிலின் அறிமுகம் மற்றும் வாக்குப்பதிவு முன்நாள் பரவலை உள்ளடக்கியது.",
    url:
      "https://www.mylaporetimes.com/2026/04/vidya-mandirs-kapil-bhaskar-builds-voter-info-platform-users-flowing-in-on-polling-eve/",
    domain: "mylaporetimes.com",
    color: "#154d38",
  },
  {
    key: "tms",
    source: "Tell My Story",
    title_en:
      "The Nee Yosi Effect — How a Chennai teen helped voters navigate 4,000 candidates",
    title_ta:
      "நீயோசி விளைவு — சென்னை மாணவர் 4,000 வேட்பாளர்களை வாக்காளர்களுக்கு எப்படி எளிதாக்கினார்",
    blurb_en:
      "A long-form feature on the story, mission and reach of NeeYosi.",
    blurb_ta:
      "நீயோசியின் கதை, நோக்கம் மற்றும் அடைந்த வெற்றியைப் பற்றிய நீண்ட கட்டுரை.",
    url:
      "https://tellmystory.in/nee-yosi-effect-how-a-chennai-teen-helped-voters-navigate-4000-candidates/",
    domain: "tellmystory.in",
    color: "#e83e8c",
  },
  {
    key: "newsbytes",
    source: "NewsBytes",
    title_en:
      "Chennai student Kapil Bhaskar created neeyosi.com for Tamil Nadu voters",
    title_ta:
      "சென்னை மாணவர் கபில் பாஸ்கர் தமிழக வாக்காளர்களுக்காக neeyosi.com-ஐ உருவாக்கினார்",
    blurb_en:
      "TLDR-style science/tech coverage on NewsBytes.",
    blurb_ta:
      "NewsBytes — சுருக்கமான அறிவியல்/தொழில்நுட்ப செய்தி.",
    url:
      "https://www.newsbytesapp.com/news/science/chennai-student-kapil-bhaskar-created-neeyosicom-for-tamil-nadu-voters/tldr",
    domain: "newsbytesapp.com",
    color: "#1abc9c",
  },
];

function favicon(domain) {
  return `https://www.google.com/s2/favicons?sz=128&domain=${domain}`;
}

function MediaCard({ item, ta }) {
  const title = ta ? item.title_ta : item.title_en;
  const blurb = ta ? item.blurb_ta : item.blurb_en;
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group block bg-white rounded-[18px] overflow-hidden shadow-sm hover:shadow-lg transition-all active:scale-[0.995]"
      data-testid={`media-card-${item.key}`}
    >
      {/* Top color band with logo */}
      <div
        className="h-24 flex items-center justify-between px-5 relative"
        style={{
          background: `linear-gradient(135deg, ${item.color} 0%, ${item.color}dd 100%)`,
        }}
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-white flex items-center justify-center overflow-hidden shrink-0 shadow-sm">
            <img
              src={favicon(item.domain)}
              alt={`${item.source} logo`}
              className="w-7 h-7 object-contain"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </div>
          <div className="text-white font-semibold text-[15px] leading-tight tracking-tight">
            {item.source}
          </div>
        </div>
        {item.isVideo && (
          <div className="rounded-full bg-white/20 backdrop-blur px-2.5 py-1 text-[11px] font-semibold text-white flex items-center gap-1">
            <Play size={12} strokeWidth={2.6} fill="currentColor" />
            {ta ? "வீடியோ" : "Video"}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-5">
        <h3
          className={`text-[16px] sm:text-[17px] font-semibold text-[#1a1640] leading-snug tracking-tight ${
            ta ? "font-tamil" : "font-display"
          }`}
          data-testid={`media-title-${item.key}`}
        >
          {title}
        </h3>
        <p
          className={`mt-2 text-[13px] leading-relaxed text-[#6b6894] ${
            ta ? "font-tamil" : ""
          }`}
        >
          {blurb}
        </p>
        <div className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#1a1640] group-hover:text-[#5b21b6] transition">
          {ta ? "கட்டுரையைப் படிக்க" : "Read article"}
          <ExternalLink size={14} strokeWidth={2.4} />
        </div>
      </div>
    </a>
  );
}

export default function Media() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const ta = lang === "ta";

  return (
    <div className="App">
      <Header compact />
      <QuoteBanner />

      <main className="max-w-5xl mx-auto px-6 pt-8 pb-24">
        <button
          onClick={() => navigate(-1)}
          className={`mb-4 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-[13px] font-semibold text-[#1a1640] shadow-sm hover:bg-[#f3eedc] active:scale-[0.99] transition ${
            ta ? "font-tamil" : ""
          }`}
          data-testid="media-back-btn"
        >
          <ArrowLeft size={16} strokeWidth={2.4} />
          {t("contact_back")}
        </button>

        <h1
          className={`text-3xl sm:text-4xl font-semibold text-[#1a1640] tracking-tight ${
            ta ? "font-tamil" : "font-display"
          }`}
          data-testid="media-title"
        >
          {ta ? "ஊடகக் குறிப்புகள்" : "In the News"}
        </h1>
        <p
          className={`mt-2 text-[14px] sm:text-[15px] text-[#6b6894] ${
            ta ? "font-tamil" : ""
          }`}
          data-testid="media-subtitle"
        >
          {ta
            ? "NeeYosi-யைப் பற்றி முன்னணி ஊடகங்கள் என்ன சொல்கின்றன."
            : "What leading publications are saying about NeeYosi."}
        </p>

        {/* Featured — Girish Mathrubootham LinkedIn */}
        <a
          href={FEATURED.url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-8 block relative rounded-[22px] overflow-hidden shadow-md hover:shadow-xl transition-all active:scale-[0.998] group"
          data-testid={`media-card-${FEATURED.key}`}
          style={{
            background:
              "linear-gradient(135deg,#0a66c2 0%,#1e40af 55%,#1a1640 100%)",
          }}
        >
          <div className="grain-overlay" />
          <div className="relative z-10 p-6 sm:p-8 text-white">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur px-2.5 py-1 text-[11px] font-semibold tracking-wide uppercase">
              <Star size={12} strokeWidth={2.6} fill="currentColor" />
              {ta ? "சிறப்புக் குறிப்பு" : "Featured mention"}
            </div>

            <div className="mt-5 flex items-start gap-4">
              <Quote
                size={44}
                strokeWidth={1.6}
                className="text-white/40 shrink-0 -mt-1"
              />
              <div className="min-w-0">
                <p
                  className={`text-[19px] sm:text-[22px] leading-snug font-semibold tracking-tight ${
                    ta ? "font-tamil" : "font-display"
                  }`}
                  data-testid="media-featured-quote"
                >
                  {ta ? FEATURED.title_ta : FEATURED.title_en}
                </p>
                <div className="mt-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/95 flex items-center justify-center overflow-hidden shrink-0">
                    <img
                      src={favicon(FEATURED.domain)}
                      alt="LinkedIn"
                      className="w-6 h-6 object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  </div>
                  <div>
                    <div className="text-[15px] font-semibold leading-tight">
                      {FEATURED.author}
                    </div>
                    <div
                      className={`text-[12px] text-indigo-100/85 ${
                        ta ? "font-tamil" : ""
                      }`}
                    >
                      {ta ? FEATURED.role_ta : FEATURED.role_en}
                    </div>
                  </div>
                </div>
                <div className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-white/95 group-hover:text-white transition">
                  {ta ? "LinkedIn-இல் பார்க்க" : "View on LinkedIn"}
                  <ExternalLink size={14} strokeWidth={2.4} />
                </div>
              </div>
            </div>
          </div>
        </a>

        {/* Grid of remaining mentions */}
        <div
          className="mt-8 grid gap-4 sm:gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
          data-testid="media-grid"
        >
          {ITEMS.map((it) => (
            <MediaCard key={it.key} item={it} ta={ta} />
          ))}
        </div>
      </main>

      <Footer />
    </div>
  );
}
